import { useSessionTyped } from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons'
import {
  SimpleInput,
  SimpleSelect,
  SimpleTextLink,
} from '@dreamstack/simple-components'
import { trpc } from '@dreamstack/trpc/client'
import MenuItem from '@material-ui/core/MenuItem'
import Skeleton from '@material-ui/lab/Skeleton'
import filter from 'lodash/filter'
import join from 'lodash/join'
import type { FunctionComponent } from 'react'
import { useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import tw, { styled } from 'twin.macro'
import { FormCheckbox } from '../forms/FormCheckbox'
import {
  AnimateIconTranslationRight,
  useAnimateIconTranslationRight,
  useSuccessDialog,
} from '../index'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { BodyText, Css_BodyText, HeadingH3 } from '../layout/Texts'
import { usePropAndProjectContactFormSchema } from './usePropAndProjectContactFormSchema'
import { useSubmitContactForm } from './useSubmitContactForm'

const FormFields = tw.div`grid grid-cols-1 gap-px-24 md:(grid-cols-2)`

const StyledLink = styled(SimpleTextLink)<{}>(({}) => [
  Css_BodyText,
  tw` text-accentroAqua-full font-semibold pl-px-4`,
])

const StyledFormContainer = styled.div<{
  isOnDarkBg: boolean
}>(({ isOnDarkBg }) => [
  tw`p-px-20 pb-px-48 mt-px-32 md:(px-px-40 ) ml:(px-px-48)`,
  isOnDarkBg
    ? tw`bg-accentroDarkGray-full (md:mt-0)`
    : tw`bg-accentroAquaVeryLight-full -mx-px-20 md:-mx-px-40 ml:-mx-px-48`,
])

//TODO: extract PrefilledValues and NameInput component into its own file ?

const PrefilledValues: FunctionComponent<
  React.PropsWithChildren<{
    loading: boolean
    name: string
    email?: string
  }>
> = ({ loading, name, email }) => {
  const t = useTranslation()
  return (
    <div tw="flex space-x-px-16 md:space-x-px-32">
      <div tw="min-width[50px]">
        {/* if fetching contact data -> show loading skeleton*/}
        {loading ? (
          <BodyText tw="flex flex-col font-semibold">
            <Skeleton animation={'wave'} tw="w-3/4" />
            <Skeleton animation={'wave'} tw="w-3/4" />
          </BodyText>
        ) : (
          <BodyText tw="flex flex-col font-semibold">
            {/* In some cases, the user has no name property saved in propstack, only show name if it exists in propstack*/}
            {name && <span>Name</span>}
            <span>{`${t('accentro:contactform.email')}`}</span>
          </BodyText>
        )}
      </div>
      <div tw="min-width[50px]">
        {/* if fetching contact data -> show loading skeleton*/}
        {loading ? (
          <BodyText tw="flex flex-col">
            <Skeleton animation={'wave'} tw="w-full" />
            <Skeleton animation={'wave'} tw="w-full" />
          </BodyText>
        ) : (
          <BodyText tw="flex flex-col">
            {name && <span>{name}</span>}
            <span>{email ?? ''}</span>
          </BodyText>
        )}
      </div>
    </div>
  )
}

const NameInput: FunctionComponent<
  React.PropsWithChildren<{ control: any; isOnDarkBg: boolean }>
> = ({ control, isOnDarkBg }) => {
  const t = useTranslation()
  return (
    <>
      <div tw="col-start-1">
        <Controller
          defaultValue=""
          name="clientSalutation"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              label={t('accentro:contactform.salutation')}
              formControlProps={{
                variant: 'filled',
                fullWidth: true,
              }}
              isOnDarkBg={isOnDarkBg}
              selectProps={{
                disableUnderline: true,
                onChange,
                value,
              }}
            >
              <MenuItem value={t('accentro:contactform.mr')}>
                {t('accentro:contactform.mr')}
              </MenuItem>
              <MenuItem value={t('accentro:contactform.ms')}>
                {t('accentro:contactform.ms')}
              </MenuItem>
            </SimpleSelect>
          )}
        />
      </div>
      <div tw="col-start-1">
        <Controller
          defaultValue=""
          name="clientFirstName"
          control={control}
          render={({ field }) => (
            <SimpleInput
              {...field}
              label={`${t('accentro:contactform.firstname')}*`}
              fullWidth
              isOnDarkBg={isOnDarkBg}
            />
          )}
        />
      </div>
      <Controller
        defaultValue=""
        name="clientLastName"
        control={control}
        render={({ field }) => (
          <SimpleInput
            {...field}
            label={`${t('accentro:contactform.lastname')}*`}
            fullWidth
            isOnDarkBg={isOnDarkBg}
          />
        )}
      />
    </>
  )
}

export const PropAndProjectContactForm: FunctionComponent<
  React.PropsWithChildren<{
    propId?: number
    projectId?: number
    isOnDarkBg?: boolean
  }>
> = ({ propId, projectId, isOnDarkBg = false }) => {
  const t = useTranslation()
  const yupSchemaResolver = usePropAndProjectContactFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const { handleSubmit, control, reset, watch } = methods
  const path = t('accentro:contactform.pathReg')
  const { controls } = useAnimateIconTranslationRight()
  const requestPhone = watch('requestPhone')

  const session = useSessionTyped()
  const loggedIn = !!session

  const { data: contactData, isLoading: contactDataLoading } =
    trpc.contact.getContact.useQuery(undefined, {
      enabled: loggedIn,
      trpc: { ssr: false },
      staleTime: 0,
    })

  const { SuccessDialog, show } = useSuccessDialog({
    title: t('accentro:contactform.modalTitle'),
    text: t('accentro:contactform.successShort'),
    ctaLabel: t('accentro:contactform.modalCta'),
  })
  const onSuccess = () => {
    show()
    reset()
  }

  const { submitFn, loading } = useSubmitContactForm({
    successCallback: onSuccess,
    formId: 'propertyAndProjectContact',
    type: 'contactform',
    propertyId: propId,
    projectId: projectId,
  })

  const onSubmit = useCallback(
    async (data: any) => {
      let fullData = data
      if (loggedIn) {
        if (contactData?.first_name) {
          fullData = {
            ...data,
            //TODO: possibly change contact api in future and get user data from session object instead of propstack
            clientEmail: contactData?.email,
            clientFirstName: contactData?.first_name,
            clientLastName: contactData?.last_name,
          }
        } else {
          fullData = {
            ...data,
            clientEmail: contactData?.email,
          }
        }
      }
      submitFn(fullData)
    },
    [contactData, loggedIn, submitFn]
  )

  return (
    <StyledFormContainer isOnDarkBg={isOnDarkBg}>
      {SuccessDialog}
      <div tw="my-px-24 md:(my-px-32) flex flex-col flex-wrap">
        <BodyText>{t('accentro:contactform.infoText')}</BodyText>
        {/* TODO: This TODO is from 4 months ago about when myAccentro Login is implemented....which is the case now! :) -> should it be added? */}
        {/* TODO: uncomment when MyAccentro Login is implemented */}
        {/* <div tw="md:flex">
          <BodyText>{t('accentro:contactform.loginPrompt')}</BodyText>
          <Link href={path} passHref legacyBehavior>
            <StyledLink target="_blank">
              {t('accentro:contactform.loginHere')}
            </StyledLink>
          </Link>
        </div> */}
      </div>
      <HeadingH3 tw="mb-px-20 md:(mb-px-24)">
        {loggedIn
          ? t('accentro:contactform.inquiry')
          : t('accentro:contactform.contactform')}
      </HeadingH3>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormFields>
            {loggedIn ? (
              // User loggedIn -> prefill his available name and email user data- if no name saved, only show email
              <>
                <PrefilledValues
                  name={join(
                    filter([contactData?.last_name, contactData?.first_name]),
                    ', '
                  )}
                  email={contactData?.email}
                  loading={contactDataLoading}
                />
                {!contactDataLoading &&
                  // if no name exists in propstack, show name input field
                  !contactData?.first_name && (
                    <NameInput control={control} isOnDarkBg={isOnDarkBg} />
                  )}
              </>
            ) : (
              // User not loggedIn -> Show name and email input forms to fill out user data
              <>
                <NameInput control={control} isOnDarkBg={isOnDarkBg} />
                <div tw="col-span-full">
                  <Controller
                    defaultValue=""
                    name="clientEmail"
                    control={control}
                    render={({ field }) => (
                      <SimpleInput
                        {...field}
                        label={`${t('accentro:contactform.email')}*`}
                        fullWidth
                        isOnDarkBg={isOnDarkBg}
                      />
                    )}
                  />
                </div>
              </>
            )}
            <div tw="col-span-full">
              <Controller
                defaultValue=""
                name="text"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.message')}*`}
                    multiline
                    minRows={4}
                    fullWidth
                    isOnDarkBg={isOnDarkBg}
                  />
                )}
              />
            </div>
            <div tw="col-span-full">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                propertyName="requestPhone"
                labelComponent={`${t('accentro:contactform.requestPhone')}`}
              />
            </div>
            {requestPhone && (
              <div tw="col-span-full">
                <Controller
                  defaultValue=""
                  name="clientPhone"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={`${t('accentro:contactform.phone')}*`}
                      fullWidth
                      isOnDarkBg={isOnDarkBg}
                    />
                  )}
                />
              </div>
            )}
            <div tw="col-span-full">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                propertyName="consent"
                labelComponent={`${t('accentro:contactform.consentText')}*`}
              />
            </div>
            <div tw="col-span-full pb-px-24">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                tw="flex self-start"
                propertyName="subscribeNewsletter"
                labelComponent={`${t(
                  'accentro:contactform.consentNewsletterText'
                )}`}
              />
            </div>
            <SimpleMuiButton
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              tw="width[min-content] whitespace-nowrap"
              endIcon={
                <AnimateIconTranslationRight animate={controls}>
                  <ArrowRight />
                </AnimateIconTranslationRight>
              }
            >
              {t('accentro:contactform.send')}
            </SimpleMuiButton>
          </FormFields>
        </form>
      </FormProvider>
    </StyledFormContainer>
  )
}
